import { useState, useEffect } from 'react';
import './App.css';
import SalesDashboard from './SalesDashboard';
import CustomerPortal from './CustomerPortal';
import AdminPortal from './AdminPortal';

const REMEMBER_KEY = 'yhd_remembered_username';

function LoginModal({ onClose, onSuccess }) {
  const [username, setUsername] = useState(() => localStorage.getItem(REMEMBER_KEY) || '');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(() => !!localStorage.getItem(REMEMBER_KEY));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleLogin(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (remember) {
      localStorage.setItem(REMEMBER_KEY, username);
    } else {
      localStorage.removeItem(REMEMBER_KEY);
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: username, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Invalid username or password.');
        setLoading(false);
        return;
      }
      onSuccess(data.user, data.accessToken);
    } catch {
      setError('Unable to reach the server. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.card} onClick={(e) => e.stopPropagation()}>
        <button style={modalStyles.closeBtn} onClick={onClose} aria-label="Close">&#x2715;</button>
        <img src="/yhd_logo.png" alt="Yellowhammer Data" style={modalStyles.logo} />
        <h2 style={modalStyles.title}>Login</h2>
        <p style={modalStyles.subtitle}>Sign in to view your dashboards</p>
        <form onSubmit={handleLogin} style={modalStyles.form}>
          <div style={modalStyles.field}>
            <label style={modalStyles.label}>Username</label>
            <input
              style={modalStyles.input}
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div style={modalStyles.field}>
            <label style={modalStyles.label}>Password</label>
            <input
              style={modalStyles.input}
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <label style={modalStyles.rememberWrap}>
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              style={{ accentColor: '#F5A623', width: '15px', height: '15px', cursor: 'pointer' }}
            />
            <span style={modalStyles.rememberLabel}>Remember me</span>
          </label>
          {error && <div style={modalStyles.error}>{error}</div>}
          <button style={{ ...modalStyles.submitBtn, opacity: loading ? 0.7 : 1 }} type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <p style={modalStyles.forgotWrap}>
          <a href="mailto:contactus@yellowhammerdata.com" style={modalStyles.forgotLink}>
            Forgot your password? Contact us
          </a>
        </p>
      </div>
    </div>
  );
}

function App() {
  const [loginOpen, setLoginOpen] = useState(false);
  const [view, setView] = useState('home');
  const [authToken, setAuthToken] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  function handleLoginSuccess(user, token) {
    setAuthToken(token);
    setAuthUser(user);
    setLoginOpen(false);
    setView(user.role === 'admin' ? 'admin' : 'portal');
  }

  async function handleExit() {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch { /* best-effort — clear local state regardless */ }
    setAuthToken(null);
    setAuthUser(null);
    setView('home');
  }

  async function handleUnauthorized() {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) { await handleExit(); return; }
      const data = await res.json();
      setAuthToken(data.accessToken);
    } catch {
      await handleExit();
    }
  }

  if (view === 'admin') {
    return <AdminPortal user={authUser} token={authToken} onExit={handleExit} onUnauthorized={handleUnauthorized} />;
  }

  if (view === 'portal') {
    return <CustomerPortal onExit={handleExit} />;
  }

  return (
    <div>

      {/* NAV */}
      <nav style={styles.nav}>
        <div style={styles.logo}>
          <img src="/yhd_logo.png" alt="Yellowhammer Data" style={styles.logoImg} />
        </div>

        {!isMobile && (
          <ul style={styles.navLinks}>
            <li><a href="#about" style={styles.navLink}>About</a></li>
            <li><a href="#services" style={styles.navLink}>Services</a></li>
            <li><a href="#pricing" style={styles.navLink}>Plans</a></li>
            <li><a href="#demo" style={styles.navLink}>Demo</a></li>
            <li><a href="#how-it-works" style={styles.navLink}>How It Works</a></li>
            <li><a href="#contact" style={{ ...styles.navLink, ...styles.navCta }}>Get Started</a></li>
            <li>
              <button onClick={() => setLoginOpen(true)} style={styles.navLoginBtn}>Login</button>
            </li>
            <li>
              <button onClick={() => setView('portal')} style={styles.navPortalBtn}>Client Portal</button>
            </li>
          </ul>
        )}

        {isMobile && (
          <button
            style={styles.hamburger}
            onClick={() => setMenuOpen(o => !o)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        )}

        {isMobile && menuOpen && (
          <div style={styles.mobileMenu}>
            <a href="#about"      style={styles.mobileNavLink} onClick={() => setMenuOpen(false)}>About</a>
            <a href="#services"   style={styles.mobileNavLink} onClick={() => setMenuOpen(false)}>Services</a>
            <a href="#pricing"    style={styles.mobileNavLink} onClick={() => setMenuOpen(false)}>Plans</a>
            <a href="#demo"       style={styles.mobileNavLink} onClick={() => setMenuOpen(false)}>Demo</a>
            <a href="#how-it-works" style={styles.mobileNavLink} onClick={() => setMenuOpen(false)}>How It Works</a>
            <a href="#contact"    style={{ ...styles.mobileNavLink, color: '#F5A623', fontWeight: 700, border: 'none' }} onClick={() => setMenuOpen(false)}>Get Started</a>
            <div style={{ display: 'flex', gap: '10px', paddingTop: '14px' }}>
              <button onClick={() => { setLoginOpen(true); setMenuOpen(false); }} style={{ ...styles.navLoginBtn, flex: 1 }}>Login</button>
              <button onClick={() => { setView('portal'); setMenuOpen(false); }} style={{ ...styles.navPortalBtn, flex: 1 }}>Client Portal</button>
            </div>
          </div>
        )}
      </nav>

      {loginOpen && <LoginModal onClose={() => setLoginOpen(false)} onSuccess={handleLoginSuccess} />}

      {/* HERO */}
      <section style={styles.hero}>
        <div style={styles.heroInner}>
          <div style={styles.heroContent}>
            <p style={styles.heroEyebrow}>Data Visualization & Business Intelligence</p>
            <h1 style={styles.heroH1}>
              Discovering Your<br />
              <span style={{ color: '#F5A623' }}>Business's Future</span>
            </h1>
            <p style={styles.heroP}>
              Yellowhammer Data turns your raw data — messy or clean — into clear visualizations
              that reveal peaks, trends, opportunities, and your path forward.
            </p>
            <div style={styles.heroBtns}>
              <a href="#contact" style={styles.btnPrimary}>Get a Free Consultation</a>
              <a href="#services" style={styles.btnSecondary}>See What We Do</a>
              <button onClick={() => setView('portal')} style={styles.btnPortal}>Client Portal</button>
            </div>
          </div>
          {!isMobile && (
            <div style={styles.heroLogoWrap}>
              <img src="/yhd_logo.png" alt="Yellowhammer Data" style={styles.heroLogo} />
            </div>
          )}
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" style={styles.about}>
        <div style={styles.container}>
          <div style={{ ...styles.aboutGrid, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '32px' : '64px' }}>
            <div>
              <div style={styles.sectionTag}>Who We Are</div>
              <h2 style={styles.sectionH2}>Founded in Alabama,<br />Designed for Your Business</h2>
              <p style={styles.aboutP}>
                Yellowhammer Data is an Alabama company through and through. Our team was educated
                at the <strong>University of Alabama</strong> and has spent years working with
                businesses across this state and region — from small operations to multi-location
                companies across industries like finance, retail, and energy.
              </p>
              <p style={styles.aboutP}>
                That local experience shaped how we approach every engagement. We understand the
                businesses here, and we use tools like <strong>Databricks</strong> and{' '}
                <strong>Power BI</strong> to give them the kind of data clarity that helps them
                make smarter decisions and grow with confidence.
              </p>
            </div>
            <div style={styles.aboutStats}>
              {[
                { value: 'DataBricks', label: 'Data Engineering Platform' },
                { value: 'Power BI', label: 'Visualization & Dashboards' },
                { value: '100%', label: 'Client-Focused Approach' },
              ].map((s, i) => (
                <div key={i} style={styles.statCard}>
                  <div style={styles.statValue}>{s.value}</div>
                  <div style={styles.statLabel}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" style={styles.services}>
        <div style={styles.container}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionTag}>What We Do</div>
            <h2 style={styles.sectionH2}>Everything Your Data Needs</h2>
            <p style={styles.sectionP}>
              From raw data cleanup to hosted dashboards you can access anytime,
              we handle the full picture.
            </p>
          </div>
          <div style={styles.servicesGrid}>
            {[
              {
                title: 'Data Cleanup',
                desc: 'Raw data that\'s messy, inconsistent, or unstructured gets refined and prepared so it can power accurate visualizations and dashboards.',
                detail: 'Makes your data usable',
              },
              {
                title: 'Visualization & Dashboard',
                desc: 'A custom page that brings all your key business metrics together in one place — sales, revenue, product performance, team results, and more. We want to hear what areas matter most to you.',
                detail: 'Your data, made visible',
              },
              {
                title: 'Dashboard Access',
                desc: 'We host your dashboard in our secure portal so you can log in and view your business insights whenever you want, from anywhere — no IT setup required on your end.',
                detail: 'Always-on access',
              },
              {
                title: 'Retainer Support',
                desc: 'Once you\'re making decisions based on your new data insights, we stay on retainer to keep your dashboards updated, accurate, and growing with your business.',
                detail: 'Ongoing partnership',
              },
            ].map((s, i) => (
              <div key={i} style={styles.serviceCard}>
                <div style={styles.serviceNum}>0{i + 1}</div>
                <h3 style={styles.serviceTitle}>{s.title}</h3>
                <p style={styles.serviceDesc}>{s.desc}</p>
                <div style={styles.serviceTag}>{s.detail}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" style={styles.pricing}>
        <div style={styles.container}>
          <div style={styles.sectionHeader}>
            <div style={{ ...styles.sectionTag, background: '#FFF8EC', color: '#D4891A' }}>What We Offer</div>
            <h2 style={styles.sectionH2}>Find the Right Fit for Your Business</h2>
            <p style={styles.sectionP}>Every engagement is scoped to your needs. Reach out and we'll put together a custom quote.</p>
          </div>
          <div style={styles.pricingGrid}>
            {[
              {
                name: 'One-Time Data Analysis',
                note: 'A focused, one-time engagement',
                desc: 'We dig into your data, identify what matters, and deliver a clear report of findings — trends, gaps, and opportunities your team can act on immediately.',
                features: ['Data review & cleanup', 'Key metric identification', 'Findings report & summary', 'One-time deliverable'],
                cta: 'Get a Quote',
                highlight: false,
              },
              {
                name: 'Live, Up-to-Date Dashboards',
                note: 'Ongoing access to your data',
                desc: 'A custom dashboard built around your business, hosted in our secure portal and kept current so you always have a real-time view of what\'s happening.',
                features: ['Fully custom dashboard', 'Secure portal access', 'Regular data refreshes', 'Anytime, anywhere access'],
                cta: 'Build My Dashboard',
                highlight: true,
              },
              {
                name: 'Workflows & Dashboards',
                note: 'Our most complete offering',
                desc: 'Everything in the dashboard tier, plus automated workflows that move your data, trigger reports, and keep your team informed without manual effort.',
                features: ['Custom dashboards', 'Automated data workflows', 'Scheduled reports & alerts', 'Ongoing support & updates'],
                cta: 'Let\'s Talk',
                highlight: false,
              },
            ].map((plan, i) => (
              <div key={i} style={{ ...styles.pricingCard, ...(plan.highlight ? styles.pricingCardHighlight : {}) }}>
                {plan.highlight && <div style={styles.pricingBadge}>Most Popular</div>}
                <h3 style={{ ...styles.pricingName, ...(plan.highlight ? { color: 'white' } : {}) }}>{plan.name}</h3>
                <p style={{ ...styles.pricingNote, ...(plan.highlight ? { color: 'rgba(255,255,255,0.6)' } : {}), marginTop: '6px' }}>{plan.note}</p>
                <p style={{ ...styles.pricingDesc, ...(plan.highlight ? { color: 'rgba(255,255,255,0.75)' } : {}) }}>{plan.desc}</p>
                <ul style={styles.pricingFeatures}>
                  {plan.features.map((f, j) => (
                    <li key={j} style={{ ...styles.pricingFeature, ...(plan.highlight ? { color: 'rgba(255,255,255,0.85)' } : {}) }}>
                      <span style={{ color: '#F5A623', marginRight: '8px' }}>&#10003;</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <a href="#contact" style={{ ...styles.pricingCta, ...(plan.highlight ? styles.pricingCtaHighlight : {}) }}>
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTERACTIVE DEMO */}
      <section id="demo" style={styles.demoSection}>
        <div style={styles.container}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionTag}>Live Demo</div>
            <h2 style={styles.sectionH2}>See Your Data Come to Life</h2>
            <p style={styles.sectionP}>
              Interact with this sample dashboard — filter by period or region to see how your
              real business data would respond. Customer information is shown as it would appear
              in a secured, privacy-compliant portal.
            </p>
          </div>
          <SalesDashboard />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" style={styles.howItWorks}>
        <div style={styles.container}>
          <div style={styles.sectionHeader}>
            <div style={{ ...styles.sectionTag, background: '#FFF8EC', color: '#D4891A' }}>The Process</div>
            <h2 style={styles.sectionH2}>Simple, Transparent, and Collaborative</h2>
            <p style={styles.sectionP}>No technical background required on your end — we handle everything and keep you informed every step of the way.</p>
          </div>
          <div style={styles.stepsGrid}>
            {[
              { num: '1', title: 'Discovery Conversation', desc: 'We start with a no-pressure call to understand your business and identify which questions you want your data to answer.' },
              { num: '2', title: 'We Build Your Solution', desc: 'Our team processes your data, handles any cleanup needed, and builds a custom dashboard tailored to your metrics.' },
              { num: '3', title: 'Walk-Through & Delivery', desc: 'We walk you through your new dashboard in a live session so you understand exactly what you\'re looking at and why it matters.' },
              { num: '4', title: 'Ongoing Partnership', desc: 'As your business grows and evolves, we stay on retainer to keep your data fresh, your dashboards updated, and your insights relevant.' },
            ].map((s, i) => (
              <div key={i} style={styles.step}>
                <div style={styles.stepNumber}>{s.num}</div>
                <h3 style={styles.stepTitle}>{s.title}</h3>
                <p style={styles.stepDesc}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" style={styles.contact}>
        <div style={styles.container}>
          <div style={styles.sectionHeader}>
            <div style={{ ...styles.sectionTag, background: 'rgba(245,166,35,0.2)', color: '#F5A623' }}>Let's Talk</div>
            <h2 style={{ ...styles.sectionH2, color: 'white' }}>Ready to See Your Business Clearly?</h2>
            <p style={{ ...styles.sectionP, color: 'rgba(255,255,255,0.65)' }}>Reach out today and we'll set up a free, no-obligation discovery call.</p>
          </div>
          <div style={{ ...styles.contactWrapper, gridTemplateColumns: isMobile ? '1fr' : '1fr 1.4fr', gap: isMobile ? '32px' : '56px' }}>
            <div>
              <h3 style={styles.contactInfoTitle}>Contact Information</h3>
              {[
                { label: 'Email', value: 'contactus@yellowhammerdata.com' },
                { label: 'Location', value: 'Proudly serving Alabama & beyond' },
                { label: 'Hours', value: 'Mon – Fri, 8am – 5pm CT' },
              ].map((item, i) => (
                <div key={i} style={styles.contactItem}>
                  <div style={styles.contactLabel}>{item.label}</div>
                  <div style={styles.contactValue}>{item.value}</div>
                </div>
              ))}
            </div>
            <form style={styles.contactForm} onSubmit={handleSubmit}>
              <input style={styles.input} type="text" placeholder="Your Name" required />
              <input style={styles.input} type="email" placeholder="Your Email" required />
              <input style={styles.input} type="text" placeholder="Your Business Name" />
              <select style={{ ...styles.input, color: 'rgba(255,255,255,0.6)', appearance: 'none' }} defaultValue="">
                <option value="" disabled style={{ color: '#1A2E44' }}>Interested In...</option>
                <option value="analysis" style={{ color: '#1A2E44' }}>One-Time Data Analysis</option>
                <option value="dashboards" style={{ color: '#1A2E44' }}>Live, Up-to-Date Dashboards</option>
                <option value="workflows" style={{ color: '#1A2E44' }}>Workflows &amp; Dashboards</option>
              </select>
              <textarea style={{ ...styles.input, minHeight: '120px', resize: 'vertical' }} placeholder="Tell us about your business and what you're looking to learn from your data..." />
              <button style={styles.submitBtn} type="submit">Send Message</button>
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={styles.footer}>
        <img src="/yhd_logo.png" alt="Yellowhammer Data" style={styles.footerLogo} />
        <p style={styles.footerTagline}>discovering your business's future</p>
        <p style={styles.footerCopy}>© 2026 Yellowhammer Data LLC. All rights reserved.</p>
      </footer>

    </div>
  );
}

function handleSubmit(e) {
  e.preventDefault();
  const btn = e.target.querySelector('button');
  const orig = btn.textContent;
  btn.textContent = 'Message Sent!';
  btn.style.background = '#22c55e';
  btn.style.color = 'white';
  setTimeout(() => {
    btn.textContent = orig;
    btn.style.background = '#F5A623';
    btn.style.color = '#1A2E44';
    e.target.reset();
  }, 3000);
}

const styles = {
  // NAV
  nav: { position: 'sticky', top: 0, zIndex: 100, background: 'white', borderBottom: '1px solid #e5e7eb', padding: '0 5%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '72px', boxShadow: '0 1px 8px rgba(0,0,0,0.07)' },
  logo: { display: 'flex', alignItems: 'center', textDecoration: 'none' },
  logoImg: { height: '52px', width: 'auto', objectFit: 'contain' },
  navLinks: { listStyle: 'none', display: 'flex', gap: '28px', margin: 0, padding: 0, alignItems: 'center' },
  navLink: { textDecoration: 'none', color: '#374151', fontWeight: 500, fontSize: '0.93rem', letterSpacing: '0.01em' },
  navCta: { background: '#1A2E44', color: 'white', padding: '9px 22px', borderRadius: '8px', fontWeight: 600 },
  navLoginBtn: { background: 'transparent', color: '#1A2E44', border: '2px solid #1A2E44', padding: '8px 20px', borderRadius: '8px', fontWeight: 600, fontSize: '0.93rem', cursor: 'pointer', letterSpacing: '0.01em' },
  navPortalBtn: { background: '#F5A623', color: '#1A2E44', border: 'none', padding: '8px 20px', borderRadius: '8px', fontWeight: 700, fontSize: '0.93rem', cursor: 'pointer', letterSpacing: '0.01em' },
  hamburger: { background: 'none', border: 'none', fontSize: '1.6rem', color: '#1A2E44', cursor: 'pointer', padding: '4px 8px', lineHeight: 1, display: 'flex', alignItems: 'center' },
  mobileMenu: { position: 'absolute', top: '72px', left: 0, right: 0, background: 'white', borderBottom: '1px solid #e5e7eb', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', padding: '8px 5% 20px', display: 'flex', flexDirection: 'column', zIndex: 99 },
  mobileNavLink: { display: 'block', padding: '14px 0', color: '#374151', fontWeight: 500, fontSize: '1rem', textDecoration: 'none', borderBottom: '1px solid #F3F4F6' },

  // HERO
  hero: { background: 'linear-gradient(135deg, #1A2E44 0%, #2C4A6E 100%)', color: 'white', padding: '80px 5% 72px' },
  heroInner: { maxWidth: '1100px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '48px', flexWrap: 'wrap' },
  heroContent: { flex: '1 1 440px', maxWidth: '560px' },
  heroEyebrow: { fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#F5A623', marginBottom: '16px' },
  heroH1: { fontSize: 'clamp(2.2rem, 4vw, 3.2rem)', fontWeight: 800, lineHeight: 1.12, marginBottom: '20px' },
  heroP: { fontSize: '1.08rem', color: 'rgba(255,255,255,0.78)', marginBottom: '36px', lineHeight: 1.7 },
  heroBtns: { display: 'flex', gap: '14px', flexWrap: 'wrap' },
  btnPrimary: { background: '#F5A623', color: '#1A2E44', padding: '14px 28px', borderRadius: '8px', fontWeight: 700, fontSize: '0.97rem', textDecoration: 'none', display: 'inline-block' },
  btnSecondary: { background: 'transparent', color: 'white', padding: '14px 28px', borderRadius: '8px', fontWeight: 600, fontSize: '0.97rem', textDecoration: 'none', border: '2px solid rgba(255,255,255,0.3)', display: 'inline-block' },
  btnPortal: { background: 'rgba(255,255,255,0.12)', color: 'white', padding: '14px 28px', borderRadius: '8px', fontWeight: 600, fontSize: '0.97rem', border: '2px solid rgba(255,255,255,0.3)', cursor: 'pointer', fontFamily: 'inherit' },
  heroLogoWrap: { flex: '0 1 320px', display: 'flex', justifyContent: 'center' },
  heroLogo: { width: '100%', maxWidth: '300px', filter: 'brightness(0) invert(1)' },

  // ABOUT
  about: { padding: '88px 5%', background: 'white' },
  container: { maxWidth: '1100px', margin: '0 auto' },
  aboutGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px', alignItems: 'center' },
  sectionTag: { display: 'inline-block', background: '#F0F7FF', color: '#1A2E44', padding: '4px 14px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '16px' },
  sectionH2: { fontSize: 'clamp(1.7rem, 3vw, 2.3rem)', fontWeight: 800, marginBottom: '16px', color: '#1A2E44', lineHeight: 1.2 },
  sectionP: { fontSize: '1.05rem', color: '#6B7280', maxWidth: '560px', margin: '0 auto', lineHeight: 1.7 },
  sectionHeader: { textAlign: 'center', marginBottom: '56px' },
  aboutP: { color: '#4B5563', fontSize: '1rem', lineHeight: 1.75, marginBottom: '18px' },
  aboutStats: { display: 'flex', flexDirection: 'column', gap: '16px' },
  statCard: { background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '24px 28px', borderLeft: '4px solid #F5A623' },
  statValue: { fontSize: '1.3rem', fontWeight: 800, color: '#1A2E44', marginBottom: '4px' },
  statLabel: { fontSize: '0.87rem', color: '#6B7280', fontWeight: 500 },

  // SERVICES
  services: { padding: '88px 5%', background: '#F8FAFC' },
  servicesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px' },
  serviceCard: { background: 'white', borderRadius: '14px', padding: '36px 28px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', gap: '12px' },
  serviceNum: { fontSize: '2.2rem', fontWeight: 800, color: '#F5A623', lineHeight: 1, opacity: 0.7 },
  serviceTitle: { fontSize: '1.1rem', fontWeight: 700, color: '#1A2E44', margin: 0 },
  serviceDesc: { color: '#6B7280', fontSize: '0.94rem', lineHeight: 1.65, margin: 0, flex: 1 },
  serviceTag: { display: 'inline-block', background: '#FFF8EC', color: '#D4891A', padding: '3px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600, alignSelf: 'flex-start' },

  // PRICING
  pricing: { padding: '88px 5%', background: 'white' },
  pricingGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', alignItems: 'start' },
  pricingCard: { background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: '16px', padding: '36px 28px', display: 'flex', flexDirection: 'column', gap: '8px', position: 'relative' },
  pricingCardHighlight: { background: '#1A2E44', border: '1px solid #1A2E44' },
  pricingBadge: { position: 'absolute', top: '-13px', left: '50%', transform: 'translateX(-50%)', background: '#F5A623', color: '#1A2E44', fontSize: '0.72rem', fontWeight: 700, padding: '3px 14px', borderRadius: '20px', letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' },
  pricingName: { fontSize: '1.05rem', fontWeight: 700, color: '#1A2E44', margin: 0 },
  pricingPriceRow: { display: 'flex', alignItems: 'baseline', gap: '4px', marginTop: '8px' },
  pricingPrice: { fontSize: '2.4rem', fontWeight: 800, color: '#1A2E44', lineHeight: 1 },
  pricingUnit: { fontSize: '1rem', color: '#6B7280', fontWeight: 500 },
  pricingNote: { fontSize: '0.8rem', color: '#9CA3AF', marginBottom: '8px' },
  pricingDesc: { fontSize: '0.9rem', color: '#4B5563', lineHeight: 1.65, margin: '8px 0 4px' },
  pricingFeatures: { listStyle: 'none', padding: 0, margin: '16px 0', display: 'flex', flexDirection: 'column', gap: '8px' },
  pricingFeature: { fontSize: '0.9rem', color: '#4B5563', display: 'flex', alignItems: 'flex-start', gap: '4px' },
  pricingCta: { display: 'block', textAlign: 'center', marginTop: '16px', padding: '12px', borderRadius: '8px', fontWeight: 700, fontSize: '0.9rem', textDecoration: 'none', background: '#F0F7FF', color: '#1A2E44', border: '1px solid #CBD5E1' },
  pricingCtaHighlight: { background: '#F5A623', color: '#1A2E44', border: 'none' },

  // DEMO SECTION
  demoSection: { padding: '88px 5%', background: '#F8FAFC' },

  // HOW IT WORKS
  howItWorks: { padding: '88px 5%', background: 'white' },
  stepsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '32px' },
  step: { textAlign: 'center', padding: '8px 16px' },
  stepNumber: { width: '60px', height: '60px', borderRadius: '50%', background: '#1A2E44', color: '#F5A623', fontWeight: 800, fontSize: '1.4rem', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 4px 16px rgba(26,46,68,0.2)' },
  stepTitle: { fontSize: '1.05rem', fontWeight: 700, marginBottom: '10px', color: '#1A2E44' },
  stepDesc: { color: '#6B7280', fontSize: '0.93rem', lineHeight: 1.65 },

  // CONTACT
  contact: { padding: '88px 5%', background: 'linear-gradient(135deg, #1A2E44 0%, #2C4A6E 100%)' },
  contactWrapper: { display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: '56px', maxWidth: '900px', margin: '0 auto' },
  contactInfoTitle: { color: 'white', fontWeight: 700, fontSize: '1.1rem', marginBottom: '28px', marginTop: 0 },
  contactItem: { marginBottom: '22px' },
  contactLabel: { color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '4px', fontWeight: 600 },
  contactValue: { color: 'white', fontSize: '0.97rem' },
  contactForm: { display: 'flex', flexDirection: 'column', gap: '14px' },
  input: { background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', color: 'white', borderRadius: '9px', padding: '13px 16px', fontSize: '0.95rem', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' },
  submitBtn: { background: '#F5A623', color: '#1A2E44', border: 'none', borderRadius: '9px', padding: '14px', fontWeight: 700, fontSize: '1rem', cursor: 'pointer', marginTop: '4px' },

  // FOOTER
  footer: { background: '#111827', textAlign: 'center', padding: '40px 5% 32px' },
  footerLogo: { height: '64px', width: 'auto', objectFit: 'contain', filter: 'brightness(0) invert(1)', marginBottom: '12px' },
  footerTagline: { color: '#F5A623', fontSize: '0.9rem', fontStyle: 'italic', marginBottom: '12px' },
  footerCopy: { color: 'rgba(255,255,255,0.35)', fontSize: '0.83rem', margin: 0 },
};

const modalStyles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' },
  card: { background: 'white', borderRadius: '20px', padding: '48px 40px 36px', width: '100%', maxWidth: '420px', position: 'relative', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' },
  closeBtn: { position: 'absolute', top: '16px', right: '18px', background: 'none', border: 'none', fontSize: '1.1rem', color: '#9CA3AF', cursor: 'pointer', lineHeight: 1, padding: '4px' },
  logo: { height: '56px', width: 'auto', display: 'block', margin: '0 auto 20px' },
  title: { textAlign: 'center', fontSize: '1.4rem', fontWeight: 800, color: '#1A2E44', margin: '0 0 6px' },
  subtitle: { textAlign: 'center', fontSize: '0.9rem', color: '#6B7280', margin: '0 0 28px' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '0.82rem', fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' },
  input: { border: '1.5px solid #E5E7EB', borderRadius: '9px', padding: '12px 14px', fontSize: '0.97rem', fontFamily: 'inherit', outline: 'none', color: '#1A2E44', background: '#F9FAFB' },
  error: { background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', borderRadius: '8px', padding: '10px 14px', fontSize: '0.88rem' },
  submitBtn: { background: '#F5A623', color: '#1A2E44', border: 'none', borderRadius: '9px', padding: '14px', fontWeight: 700, fontSize: '1rem', cursor: 'pointer', marginTop: '4px' },
  rememberWrap: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' },
  rememberLabel: { fontSize: '0.87rem', color: '#4B5563', userSelect: 'none' },
  forgotWrap: { textAlign: 'center', marginTop: '20px', marginBottom: 0 },
  forgotLink: { color: '#6B7280', fontSize: '0.85rem', textDecoration: 'none' },
};

export default App;
